import { StandardEvents } from '@shopify/events';
import { reconcileGiftLines } from '@theme/buy-x-get-y-sync';

/**
 * Keeps a Buy X Get Y gift line's quantity matching its trigger product's quantity when a shopper
 * edits an existing cart line — increasing/decreasing it with the quantity stepper, or removing it
 * outright — on the cart page itself. assets/buy-x-get-y.js only reacts to a *fresh* Add to Cart on
 * the product page and isn't in the DOM once the shopper leaves it, so edits made afterwards on the
 * cart page need this separate listener. Loaded only from snippets/cart-products.liquid, since
 * that's the only place a cart line's quantity can be edited.
 *
 * `assets/component-cart-items.js`'s `updateQuantity()` is the only thing that dispatches
 * `CartLinesUpdateEvent` with `context: 'cart'` — a fresh product-page add uses `'product'`, and
 * this module's own correction (via reconcileGiftLines → assets/buy-x-get-y-sync.js) uses
 * `'standard-action'`, so filtering on `'cart'` reacts only to edits made here and never to itself.
 *
 * Also runs once immediately on load — a shopper whose cart already held a gift line from before
 * this file existed would otherwise wait forever for it to self-heal, since nothing on a plain
 * page load counts as a "cart line's quantity changed" event. reconcileGiftLines() is a no-op if
 * there's nothing to fix, so this costs one harmless `/cart.js` fetch on every cart page view.
 *
 * The load-time pass both cleans up and tops up. It removes a gift that is no longer owed or no
 * longer free — a promo that ended while the item sat in the cart — and, when the promotion's rule
 * is configured and the cart already meets it, adds the gift that is due (the cart page is where a
 * cart that qualified through other pages' edits lands). Without a configured rule it can only
 * clean up: nothing could justify guessing a gift on a plain page load.
 */
reconcileGiftLines(document, { confirmRemoval: true });

document.addEventListener(StandardEvents.cartLinesUpdate, (event) => {
  if (event.context !== 'cart') return;

  event.promise
    ?.then(async ({ detail }) => {
      if (detail?.didError) return;

      // The triggering event's own morph (assets/component-cart-items.js's updateQuantity(), which
      // calls morphSection() directly rather than through sectionRenderer) is still in flight at
      // this point — its own promise resolves before that morph — and its animation
      // (`.adding`/`.removing`, or a view transition) can still be running. Firing our own
      // correction's fetch+morph immediately raced it and, on occasion, landed a duplicated row
      // (empirically reproduced, not theoretical — a fixed 400ms wasn't always enough either).
      // reconcileGiftLines() itself is a no-op if nothing has drifted out of sync by the time this
      // runs, so a longer delay here only costs a bit of lag, never correctness.
      await new Promise((resolve) => setTimeout(resolve, 900));
      reconcileGiftLines(document);
    })
    .catch((error) => {
      if (error?.name !== 'AbortError') console.warn('[buy-x-get-y-cart-sync] Event promise rejected:', error);
    });
});
